import { Injectable, } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from "@angular/router";
import * as Sentry from "@sentry/angular"
import { Observable } from 'rxjs';
import { AuthService } from "./auth.service";
import { LangsRepository } from './langs.repository';
import { isServerUnreachable } from '../error-handler/connectivity';

// one listener however many guarded navigations ran offline: addEventListener drops the duplicates
const reloadWhenOnline = () => window.location.reload();

@Injectable()
export class AuthGuard {
    constructor(
        private readonly router: Router,
        private readonly authService: AuthService,
        private langsRepository: LangsRepository,
    ) { }

    public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
        return new Promise((resolve, reject) => {
            this.authService.getMe().toPromise().then(response => {
                // No response at all: a real 401, which the interceptor already answered with the same
                // redirect (#339). It used to fall into the catch below on a TypeError (#357).
                if (!response || !response.body || response.body.responseCode !== 200 || !response.body.data) {
                    // No popup and no Sentry event: reaching a guarded route without a session is the
                    // normal way in, and the redirect below is the whole answer. It used to call
                    // showError, which put the full-screen application error over the login page -
                    // with the text "null", because an unauthenticated body carries no error - and
                    // reported a fault to Sentry on every such visit. A getMe that actually fails
                    // (network down, 500) lands in the catch below, which is silent for the same
                    // reason (#336).
                    this.authService.logout();
                    this.authService.rememberOidcRetryRoute(state.url);
                    this.router.navigateByUrl("/auth/login");
                    resolve(false);
                    return;
                }

                const serverUser = response.body.data;
                this.authService.user = serverUser;
                // Always persist server user (e.g. after company switch reload) so companyId and other fields stay in sync
                const authToken = this.authService.getToken() || "";
                const authJwt = this.authService.getJwt() || "";
                this.authService.saveAuthData({ token: authToken, jwt: authJwt, user: serverUser });

                this.langsRepository.applyServerLocale(serverUser.locale);

                if (this.authService!.user!.id) {
                    Sentry.configureScope(scope => {
                      scope.setUser({
                          id: this.authService.user.id.toString(),
                          email: this.authService.user.email,
                          username: this.authService.user.firstName + " " + this.authService.user.lastName,
                      });
                      if (this.authService.user.companyId) {
                          scope.setTag("company_id", this.authService.user.companyId.toString());
                      }
                    });
                }

                // The user is authenticated, but their session may be scoped to a different company
                // than the one the target deep link belongs to (e.g. a multi-company agency account).
                // Re-authenticate against the requested company instead of silently loading empty data.
                const requestedCompanyId = this.extractCompanyIdFromUrl(state.url);
                if (requestedCompanyId && serverUser.companyId && requestedCompanyId !== serverUser.companyId) {
                    this.authService.rememberOidcRetryRoute(state.url);
                    const callbackUrl = this.authService.getOidcCallbackUrl();
                    this.authService.startOidcLogin(callbackUrl, requestedCompanyId);
                    resolve(false);
                    return;
                }

                this.authService.clearPersistedOidcRetryRoute();
                resolve(true);
            }).catch(err => {
                // The server was not reached (no network, or a gateway answering for it), so nothing says the
                // session is over. Logging out wiped the stored one - the password again once the network was
                // back - and the login started below cannot load offline: closing its browser left the app on
                // no route, a blank screen (#336). Nothing loads offline either, hence the reload once online.
                if (isServerUnreachable(err) && this.authService.authenticated) {
                    window.addEventListener('online', reloadWhenOnline, { once: true });
                    resolve(true);
                    return;
                }

                this.authService.logout();
                this.authService.prevUrl = state.url;

                const isCallbackRoute = state.url.indexOf('/auth/callback') >= 0;
                if (isCallbackRoute) {
                    this.router.navigateByUrl("/auth/login?error=auth_failed");
                    resolve(false);
                    return;
                }

                if (isServerUnreachable(err)) {
                    // The login page opens the same browser and keeps its retry button under it.
                    this.authService.rememberOidcRetryRoute(state.url);
                    this.router.navigateByUrl("/auth/login");
                    resolve(false);
                    return;
                }

                const retryValue = this.authService.getPersistedOidcRetryRoute();
                if (retryValue === state.url) {
                    this.router.navigateByUrl("/auth/login?error=auth_failed");
                    resolve(false);
                    return;
                }

                this.authService.rememberOidcRetryRoute(state.url);
                // OIDC-first login: don't show the legacy login page.
                const callbackUrl = this.authService.getOidcCallbackUrl();
                const companyId = this.extractCompanyIdFromUrl(state.url);
                this.authService.startOidcLogin(callbackUrl, companyId);

                resolve(false);
            });
        });
    }

    private extractCompanyIdFromUrl(url: string): number | undefined {
        const queryIndex = url.indexOf('?');
        const queryString = queryIndex >= 0 ? url.substring(queryIndex + 1) : '';
        const companyIdParam = new URLSearchParams(queryString).get('companyId');
        return companyIdParam ? parseInt(companyIdParam, 10) : undefined;
    }
}
